import { ClockCircleOutlined } from '@ant-design/icons';
import { ServiceExtraForm, ServiceTypeTag, SoftTag } from '@components';
import { useView } from '@hooks';
import { Utils } from '@utils';
import { Input, InputNumber, Typography } from 'antd';
import React from 'react';
import { IServiceDetailsPresentation } from './ServiceDetailsCard.props';
import { RemoveServiceButton } from './RemoveServiceButton';
import { serviceGrid } from './serviceGrid';

const { Text } = Typography;

export const ServiceDetailsPresentation: React.FC<IServiceDetailsPresentation> = ({
  service,
  disabled,
  showDiscount,
  showTotals,
  subTotal,
  vatAmount,
  parts,
  onRemovePart,
  onRemove,
  canRemove,
  onUpdatePart,
  onAddPartToPurchaseOrder,
  handlePriceUpdate,
  handleDurationUpdate,
  handleDiscountUpdate,
  handleNoteUpdate,
}) => {
  const { isSmallDesktop } = useView();

  const lbl: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 700,
    color: 'rgba(0, 0, 0, 0.85)',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
    display: 'block',
  };

  const grid = serviceGrid({
    isSmallDesktop,
    leadingColumns: isSmallDesktop ? '1fr 110px' : '1fr 130px',
    showDiscount,
    showTotals,
  });

  return (
    <>
      {/* ── Service row (grid) ── */}
      <div
        style={{
          ...grid,
          alignItems: 'end',
          padding: '6px 10px',
          marginBottom: 4,
        }}
      >
        {/* Name + tag — span first 4 cols */}
        <div
          style={{
            gridColumn: '1 / 5',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            alignSelf: 'end',
            paddingBottom: 4,
          }}
        >
          <Text style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
            {service.name}
          </Text>
          <ServiceTypeTag
            isFixed={service.isFixed}
            style={{ fontSize: 13, padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}
          />
          {/* What the Hours field comes to: its column is too narrow to say it there as well. */}
          {!!service.duration && service.duration > 0 && (
            <Text type="secondary" title="Duration" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              {Utils.formatDecimalHours(service.duration)}
            </Text>
          )}
        </div>

        {/* Price — col 5 aligns with Sale */}
        <div>
          <Text style={lbl}>{service.isFixed ? 'Price (£)' : '£/hour'}</Text>
          <InputNumber
            min={0}
            step={1}
            precision={2}
            style={{ width: '100%' }}
            value={service.price}
            disabled={disabled}
            onChange={(v: number | null) =>
              handlePriceUpdate(service.id, v !== null ? Number(Utils.formatNumber(v, 2)) : 0)
            }
          />
        </div>

        {/* Duration, in decimal hours — col 6 aligns with Qty */}
        <div>
          <Text style={lbl}>Hours</Text>
          <InputNumber
            min={0}
            step={0.01}
            precision={2}
            style={{ width: '100%' }}
            disabled={disabled}
            value={service.duration}
            onChange={(v: number | null) =>
              handleDurationUpdate(service.id, v !== null ? Number(Utils.formatNumber(v, 2)) : 0)
            }
          />
        </div>

        {/* Discount — col 7 aligns with Disc */}
        {showDiscount && (
          <div>
            <Text style={lbl}>Disc. %</Text>
            <InputNumber
              min={0}
              max={100}
              step={1}
              precision={0}
              style={{ width: '100%' }}
              value={service.discount}
              disabled={disabled}
              onChange={(v: number | null) =>
                handleDiscountUpdate(service.id, v !== null ? Number(Utils.formatNumber(v, 2)) : 0)
              }
            />
          </div>
        )}

        {showTotals && (
          <>
            {/* Sub total */}
            <div style={{ paddingBottom: 4, textAlign: 'right' }}>
              <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block' }}>SUB TOTAL</Text>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>
                {Utils.formatCurrency(subTotal)}
              </span>
            </div>

            {/* VAT */}
            <div style={{ paddingBottom: 4, textAlign: 'right' }}>
              <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block' }}>VAT</Text>
              <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
                {Utils.formatCurrency(vatAmount)}
              </span>
            </div>

            {/* Total */}
            <div style={{ paddingBottom: 4, textAlign: 'right' }}>
              <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block' }}>TOTAL</Text>
              <SoftTag
                group="blue"
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  padding: '2px 8px',
                  margin: 0,
                  minWidth: 70,
                  textAlign: 'center',
                  display: 'inline-block',
                }}
              >
                {Utils.formatCurrency(Utils.roundToTwoDecimals(subTotal + vatAmount))}
              </SoftTag>
            </div>
          </>
        )}

        {/* Remove the service - same column as the parts' Remove below. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 4 }}>
          {onRemove && (
            <RemoveServiceButton
              iconOnly={isSmallDesktop}
              disabled={disabled || canRemove === false}
              onRemove={onRemove}
            />
          )}
        </div>
      </div>

      {/* ── Note input ── */}
      <div style={{ marginBottom: 8, padding: '0 10px' }}>
        <Text style={lbl}>Note</Text>
        <Input.TextArea
          placeholder="Note..."
          value={service.note || ''}
          disabled={disabled}
          autoSize={{ minRows: 1 }}
          onChange={(e) => handleNoteUpdate(service.id, e.target.value)}
          style={{ fontSize: 12 }}
        />
      </div>

      {/* ── Parts & extras ── */}
      {parts.length > 0 && (
        <div>
          {parts.map((servicePart, index) => (
            <ServiceExtraForm
              key={`${service.id}part${index}`}
              index={index}
              extra={servicePart}
              onRemove={() => onRemovePart(index)}
              onUpdate={(values) => onUpdatePart(index, values)}
              disabled={disabled}
              onAddToPurchaseOrder={onAddPartToPurchaseOrder ? () => onAddPartToPurchaseOrder(index) : undefined}
            />
          ))}
        </div>
      )}
    </>
  );
};
